import 'intl-tel-input/build/css/intlTelInput.css';
import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import intlTelInput, { SomeOptions, Iti } from 'intl-tel-input';
import { IInputComponent, StyledInput } from 'components/Form/Input';
import styled from 'styled-components';

export interface IProps extends IInputComponent {
  telOptions?: SomeOptions;
}

export const validatePhoneNumber = (input: HTMLInputElement | null): string => {
  if (!input) return '';

  const iti = intlTelInput.getInstance(input);
  if (!iti) return '';

  return iti.isValidNumber() ? '' : 'Phone number has an invalid format';
};

const PhoneInput: FunctionComponent<IProps> = ({
  telOptions = {
    countryOrder: ['ca', 'us'],
    initialCountry: 'ca',
    separateDialCode: true,
    loadUtilsOnInit: 'https://cdn.jsdelivr.net/npm/intl-tel-input@24.8.2/build/js/utils.js',
    autoPlaceholder: 'off',
  },
  onChange,
  onBlur,
  value,
  ...rest
}) => {
  const inputEl = useRef<HTMLInputElement>(null);
  const itiRef = useRef<Iti | null>(null);
  const lastNumberRef = useRef<string | undefined>(value);

  const changeHandler = useCallback(() => {
    const newNumber = itiRef.current?.getNumber() || '';
    if (newNumber !== lastNumberRef.current) {
      lastNumberRef.current = newNumber;
      onChange?.(newNumber);
    }
  }, [onChange]);

  const keyPressHandler = useCallback((event) => {
    if (!/[0-9\-]/.test(event.key)) {
      event.preventDefault();
    }
  }, []);

  useEffect(() => {
    const el = inputEl.current;
    if (!el) return;

    if (!itiRef.current) {
      itiRef.current = intlTelInput(el, telOptions);
      itiRef.current.setNumber(value || '');
      lastNumberRef.current = value;

      const handler = () => changeHandler();
      el.addEventListener('countrychange', handler);

      return () => {
        el.removeEventListener('countrychange', handler);
      };
    }

    if (value && value !== lastNumberRef.current) {
      itiRef.current.setNumber(value);
      lastNumberRef.current = value;
    }
  }, [telOptions, value, changeHandler]);

  return (
    <PhoneWrapper>
      <StyledInput
        ref={inputEl}
        onKeyUp={changeHandler}
        onKeyPress={keyPressHandler}
        onBlur={(e) => {
          changeHandler();
          onBlur?.(e);
        }}
        {...rest}
      />
    </PhoneWrapper>
  );
};

export default PhoneInput;

const PhoneWrapper = styled.div`
  .iti {
    display: block;
  }
  .iti__selected-country-primary {
    border-top-left-radius: 30px;
    border-bottom-left-radius: 30px;
  }
`;
